import React, { useEffect, useMemo } from "react";
import {
  Badge,
  Box,
  Center,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import FinancePageShell from "./common/FinancePageShell";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

import AdminFinanceOverviewPage from "./AdminFinanceOverviewPage";
import AdminPaymentsPage from "./AdminPaymentsPage";
import AdminSalesAllocationsPage from "./AdminSalesAllocationsPage";
import AdminDriverEarningsPage from "./AdminDriverEarningsPage";
import AdminPayoutBatchesPage from "./AdminPayoutBatchesPage";
import AdminPayoutDestinationsPage from "./AdminPayoutDestinationsPage";
import AdminLedgerAuditPage from "./AdminLedgerAuditPage";
import {
  useAdminAccess,
  AdminAccessProvider,
} from "../Analytics/shared/AdminAccess/AdminAccessContext";

type FinanceTabKey =
  | "overview"
  | "payments"
  | "sales"
  | "driver-earnings"
  | "payout-batches"
  | "payout-destinations"
  | "ledger";

type FinanceTab = {
  key: FinanceTabKey;
  label: string;
  description: string;
  superAdminOnly?: boolean;
  panel: React.ReactNode;
};

const normalizeRole = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const AdminFinanceHubContent: React.FC = () => {
  const { theme } = useCustomTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId, role, isSuperAdmin, loading } = useAdminAccess();

  const normalizedRole = normalizeRole(role);
  const hasFinanceAdminAccess = isSuperAdmin || normalizedRole === "admin";

  const financeTabs = useMemo<FinanceTab[]>(
    () => [
      {
        key: "overview",
        label: "Overview",
        description: "Platform-wide finance totals across every account.",
        superAdminOnly: true,
        panel: <AdminFinanceOverviewPage />,
      },
      {
        key: "payments",
        label: "Payments",
        description:
          "Customer payment rows linked to this account's orders and services.",
        panel: <AdminPaymentsPage />,
      },
      {
        key: "sales",
        label: "Sales",
        description:
          "Sales allocations, fees, and seller payout state linked to this account.",
        panel: <AdminSalesAllocationsPage />,
      },
      {
        key: "driver-earnings",
        label: "Drivers",
        description: "Platform-wide driver earnings and payout state.",
        superAdminOnly: true,
        panel: <AdminDriverEarningsPage />,
      },
      {
        key: "payout-batches",
        label: "Batches",
        description:
          "Create and manage platform payout batches, retries, and cancellations.",
        superAdminOnly: true,
        panel: <AdminPayoutBatchesPage />,
      },
      {
        key: "payout-destinations",
        label: "Destinations",
        description:
          "Payout accounts linked to the current account, or all accounts for superadmin.",
        panel: <AdminPayoutDestinationsPage />,
      },
      {
        key: "ledger",
        label: "Ledger",
        description:
          "Platform-wide audit of payments, reserves, payouts, refunds, and reversals.",
        superAdminOnly: true,
        panel: <AdminLedgerAuditPage />,
      },
    ],
    [],
  );

  const visibleTabs = useMemo(
    () =>
      financeTabs.filter((tab) => isSuperAdmin || tab.superAdminOnly !== true),
    [financeTabs, isSuperAdmin],
  );

  const activeTabKey = searchParams.get("tab") as FinanceTabKey | null;

  const activeIndex = useMemo(() => {
    const foundIndex = visibleTabs.findIndex((tab) => tab.key === activeTabKey);

    return foundIndex >= 0 ? foundIndex : 0;
  }, [activeTabKey, visibleTabs]);

  const activeTab = visibleTabs[activeIndex];

  useEffect(() => {
    if (loading || !hasFinanceAdminAccess || visibleTabs.length === 0) {
      return;
    }

    const requestedTab = searchParams.get("tab") as FinanceTabKey | null;
    const requestedTabIsVisible = visibleTabs.some(
      (tab) => tab.key === requestedTab,
    );

    if (requestedTab && !requestedTabIsVisible) {
      const fallbackTab = visibleTabs[0];

      if (!fallbackTab || fallbackTab.key === "overview") {
        setSearchParams({}, { replace: true });
        return;
      }

      setSearchParams({ tab: fallbackTab.key }, { replace: true });
    }
  }, [
    hasFinanceAdminAccess,
    loading,
    searchParams,
    setSearchParams,
    visibleTabs,
  ]);

  const isDark = theme.name === "dark";
  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const cardShadow = theme.shadowSm;

  const handleTabChange = (index: number) => {
    const nextTab = visibleTabs[index];

    if (!nextTab || nextTab.key === "overview") {
      setSearchParams({});
      return;
    }

    setSearchParams({ tab: nextTab.key });
  };

  if (loading) {
    return (
      <Center minH="320px">
        <Spinner />
      </Center>
    );
  }

  if (!userId) {
    return (
      <Center minH="320px">
        <Text color={mutedText}>Sign in before opening Finance Admin.</Text>
      </Center>
    );
  }

  if (!hasFinanceAdminAccess) {
    return (
      <Center minH="320px" px={4}>
        <Box
          maxW="520px"
          w="100%"
          p={6}
          borderRadius="16px"
          bg={cardBg}
          border={`1px solid ${borderColor}`}
          boxShadow={theme.shadowSm}
          textAlign="center"
        >
          <Text fontWeight="800" color={pageText}>
            Finance Admin access denied
          </Text>
          <Text mt={2} color={mutedText} fontSize="sm">
            Only admin and superadmin accounts can open this finance
            administration area.
          </Text>
        </Box>
      </Center>
    );
  }

  return (
    <FinancePageShell
      title="Finance Admin"
      subtitle={
        isSuperAdmin
          ? "Payments, sales, driver earnings, payout batches, destinations, and audit data across every account."
          : "Payments, sales, and payout destinations linked only to your account."
      }
    >
      <Box mb={4}>
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          colorScheme={isSuperAdmin ? "purple" : "blue"}
        >
          {isSuperAdmin ? "All platform accounts" : "Current account only"}
        </Badge>

        <Text mt={2} fontSize="xs" color={mutedText}>
          Role: {role || "user"} · UID: {userId}
        </Text>
      </Box>

      <Box
        bg={cardBg}
        p={5}
        borderRadius="10px"
        boxShadow={theme.shadowMd}
        border={`1px solid ${borderColor}`}
        backdropFilter={isDark ? "blur(8px)" : undefined}
      >
        <Tabs
          variant="simple"
          index={activeIndex}
          onChange={handleTabChange}
          isLazy
          lazyBehavior="keepMounted"
        >
          <Box overflowX="auto">
            <TabList
              gap={2}
              bg={softBg}
              border={`1px solid ${borderColor}`}
              mb={4}
              minW="max-content"
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  fontWeight="700"
                  color={mutedText}
                  _selected={{
                    color: pageText,
                    bg: cardBg,
                    borderTop: "2px solid #6ec5c4",
                    boxShadow: cardShadow,
                  }}
                  maxW="150px"
                  whiteSpace="nowrap"
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Box>

          {activeTab && (
            <Text mb={4} color={mutedText} fontSize="sm">
              {activeTab.description}
            </Text>
          )}

          <TabPanels>
            {visibleTabs.map((tab) => (
              <TabPanel key={tab.key} px={0}>
                {tab.panel}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    </FinancePageShell>
  );
};

const AdminFinanceHubPage: React.FC = () => (
  <AdminAccessProvider>
    <AdminFinanceHubContent />
  </AdminAccessProvider>
);

export default AdminFinanceHubPage;
